import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Shows the over-limit warning in the same slot/style as the cart's native
   * inventory error (the `.cart-items__error` cell rendered next to the quantity cell).
   * @param {number} maxQuantity - The maximum allowed quantity.
   */
  showQuantityError(maxQuantity) {
    const quantityCell = this.closest('.cart-items__quantity');
    const errorContainer = quantityCell?.nextElementSibling;

    if (!(errorContainer instanceof HTMLElement) || !errorContainer.classList.contains('cart-items__error')) {
      super.showQuantityError(maxQuantity);
      return;
    }

    const errorText = errorContainer.querySelector('.cart-item__error-text');
    const template = this.dataset.quantityErrorMax || '';
    const message = template.replace('{{ maximum }}', maxQuantity.toString());

    if (errorText) errorText.textContent = message;
    errorContainer.classList.remove('hidden');

    if (this.quantityErrorTimeout) clearTimeout(this.quantityErrorTimeout);
    this.quantityErrorTimeout = setTimeout(() => {
      errorContainer.classList.add('hidden');
    }, 5000);
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
